import React, { MutableRefObject } from 'react';
import { Message } from '../types';
import AudioPlayer from './AudioPlayer';
import TypingIndicator from './TypingIndicator';
import MarkdownRenderer from './MarkdownRenderer';


interface ChatMessagesProps {
  messages: Message[];
  messagesEndRef: MutableRefObject<HTMLDivElement | null>;
  messagesContainerRef: MutableRefObject<HTMLDivElement | null>;
  isLoadingSessions: boolean;
  hasMoreMessages: boolean;
  isLoadingOlderMessages: boolean;
  onLoadMoreMessages: () => void;
  selectedSubject: string;
  isAudioPlaying: boolean;
  setIsAudioPlaying: (value: boolean) => void;
  botAudioRef: MutableRefObject<HTMLAudioElement | null>;
  isBotAudioManuallyPlaying: boolean;
  setIsBotAudioManuallyPlaying: (value: boolean) => void;
  isLastBotResponseComplete: boolean;
  setIsLastBotResponseComplete: (value: boolean) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  messagesEndRef,
  messagesContainerRef,
  isLoadingSessions,
  hasMoreMessages,
  isLoadingOlderMessages,
  onLoadMoreMessages,
  selectedSubject,
  isAudioPlaying,
  setIsAudioPlaying,
  botAudioRef,
  isBotAudioManuallyPlaying,
  setIsBotAudioManuallyPlaying,
  isLastBotResponseComplete,
  setIsLastBotResponseComplete,
}) => {
  const renderMessageContent = (message: Message, index: number) => {
    const lastAssistantIndex = (() => {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (!messages[i].isUser) return i;
      }
      return -1;
    })();
    const isLastAssistant = index === lastAssistantIndex && !message.isUser;
    const hasTextContent = Boolean(message.content && message.content.trim().length);
    const hasImages = Boolean(message.images && message.images.length > 0);
    const shouldShowTypingIndicator = !message.isUser && !hasTextContent && !hasImages;
    const hasAssistantAudio = !message.isUser && Boolean(message.audio?.audioUrl);
    const shouldRenderPlainText = message.isUser || message.renderMode === 'plainText';

    return (
      <div className="message-content-wrapper">
        {shouldShowTypingIndicator && <TypingIndicator />}

        {hasTextContent && (
          <div className="message-text">
            {shouldRenderPlainText ? (
              <p style={{ whiteSpace: 'pre-wrap' }}>{message.content}</p>
            ) : (
              <MarkdownRenderer content={message.content} />
            )}

            {isLastAssistant && isAudioPlaying && (
              <button
                className="stop-bot-audio-btn"
                style={{
                  marginLeft: 16,
                  padding: '4px 16px',
                  fontSize: 14,
                  fontWeight: 'bold',
                  background: '#f44',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (botAudioRef.current) {
                    botAudioRef.current.pause();
                    botAudioRef.current.currentTime = 0;
                    setIsAudioPlaying(false);
                    botAudioRef.current = null;
                    setIsBotAudioManuallyPlaying(false);
                    if (!isLastBotResponseComplete) {
                      setIsLastBotResponseComplete(false);
                    }
                  }
                }}
              >
                Stop Audio
              </button>
            )}

            {isLastAssistant && !isAudioPlaying && hasAssistantAudio && isLastBotResponseComplete && (
              <div style={{ marginTop: 12 }}>
                <button
                  className="play-bot-audio-btn"
                  style={{
                    padding: '4px 16px',
                    fontSize: 14,
                    fontWeight: 'bold',
                    background: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (!message.audio?.audioUrl) {
                      return;
                    }
                    if (!isBotAudioManuallyPlaying) {
                      if (botAudioRef.current) {
                        botAudioRef.current.pause();
                        botAudioRef.current.currentTime = 0;
                      }
                      const audio = new Audio(message.audio.audioUrl);
                      botAudioRef.current = audio;
                      setIsAudioPlaying(true);
                      setIsBotAudioManuallyPlaying(true);
                      audio.play();
                      audio.onended = () => {
                        setIsAudioPlaying(false);
                        setIsBotAudioManuallyPlaying(false);
                        botAudioRef.current = null;
                      };
                    } else if (botAudioRef.current) {
                      botAudioRef.current.pause();
                      botAudioRef.current.currentTime = 0;
                      setIsAudioPlaying(false);
                      setIsBotAudioManuallyPlaying(false);
                      botAudioRef.current = null;
                    }
                  }}
                >
                  {isBotAudioManuallyPlaying ? 'Stop Audio' : 'Play Audio'}
                </button>
              </div>
            )}
          </div>
        )}

        {!message.isUser &&
          message.topicImagesLoading &&
          !(message.topicImages && message.topicImages.length > 0) && (
            <div className="topic-image-block" aria-busy="true">
              <span className="topic-image-skeleton-caption" />
              <div className="topic-image-grid">
                <span className="topic-image-skeleton" />
                <span className="topic-image-skeleton" />
              </div>
            </div>
          )}
        {!message.isUser && message.topicImages && message.topicImages.length > 0 && (
          <div className="topic-image-block">
            <span className="topic-image-caption">From Wikipedia</span>
            <div className="topic-image-grid">
              {message.topicImages.map((url, i) => (
                <a
                  key={`${url}-${i}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="topic-image-link"
                >
                  <img
                    src={url}
                    alt="Related visual"
                    loading="lazy"
                    className="topic-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.style.display = 'none';
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {message.images && message.images.length > 0 && (
          <div className="message-image-grid">
            {message.images.map((img) => (
              <div key={img.id} className="message-image-wrapper">
                <img
                  src={img.preview}
                  alt="Shared content"
                  loading="lazy"
                  onClick={() => window.open(img.preview, '_blank')}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src =
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk0OTQ5NCIgZm9udC1mYW1pbHk9InN5c3RlbS11aSwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNnB4Ij5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {message.audio && message.isUser && (
          <div className="message-audio">
            <AudioPlayer audioUrl={message.audio.audioUrl} duration={message.audio.duration} autoPlay={false} />
          </div>
        )}

        {(message.content || message.isUser) && (
          <span className="timestamp">
            {message.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className="messages-container"
      ref={(node) => {
        messagesContainerRef.current = node;
      }}
    >
      {hasMoreMessages && messages.length > 0 && (
        <div className="load-more-messages">
          <button
            className="load-more-button"
            onClick={onLoadMoreMessages}
            disabled={isLoadingOlderMessages}
          >
            {isLoadingOlderMessages ? 'Loading previous messages…' : 'View earlier messages'}
          </button>
        </div>
      )}
      {isLoadingSessions && messages.length === 0 && (
        <div className="welcome-message">
          <p>Loading session messages...</p>
        </div>
      )}

      {!isLoadingSessions && messages.length === 0 && (
        <div className="welcome-message">
          <p>Welcome to your {selectedSubject} tutoring session! How can I help you today?</p>
        </div>
      )}

      {messages.map((message, index) => (
        <div key={index} className={`message ${message.isUser ? 'user-message' : 'ai-message'}`}>
          {renderMessageContent(message, index)}
        </div>
      ))}

      <div
        ref={(node) => {
          // Update the forwarded ref manually so parent keeps scroll anchor
          messagesEndRef.current = node;
        }}
      />
    </div>
  );
};

export default ChatMessages;
